import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { auth, db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut} from 'firebase/auth';

// Письма Firebase — на русском
auth.languageCode = 'ru';
export const SESSION_KEY = 'gji_login_time';
export const CORPORATE_DOMAIN = '@gzhi.gov.spb.ru';

const checkCorporateEmail = (email) => {
  if (!email || !email.trim().toLowerCase().endsWith(CORPORATE_DOMAIN)) {
    throw new Error('Вход и регистрация разрешены только сотрудникам с почтой ' + CORPORATE_DOMAIN);
  }
};
const translateAuthError = (code) => {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'Эта почта уже зарегистрирована. Нажмите «Войти» вместо регистрации.';
    case 'auth/invalid-email':
      return 'Некорректный формат email.';
    case 'auth/weak-password':
      return 'Слишком простой пароль. Используйте минимум 6 символов.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Неверный email или пароль.';
    case 'auth/too-many-requests':
      return 'Слишком много попыток. Подождите немного и повторите.';
    case 'auth/network-request-failed':
      return 'Нет соединения с сервером. Проверьте интернет.';
    default:
      return 'Ошибка авторизации. Проверьте данные или обратитесь к администратору.';
  }
};
export const loginUser = createAsyncThunk('auth/login', async ({ email, password }, { rejectWithValue }) => {
  try {
    // Вход без проверки домена — пускаем любые существующие учётки
    const userCredential = await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
    localStorage.setItem(SESSION_KEY, String(Date.now()));
    return userCredential.user;
  } catch (error) {
    return rejectWithValue(translateAuthError(error.code));
  }
});

export const registerUser = createAsyncThunk('auth/register', async ({ email, password }, { rejectWithValue }) => {
  try {
    checkCorporateEmail(email);
    const userCredential = await createUserWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
    // Новый аккаунт получает статус "Новенький"
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      uid: userCredential.user.uid,
      email: userCredential.user.email,
      role: 'newbie',
    }, { merge: true });
    localStorage.setItem(SESSION_KEY, String(Date.now()));
    return userCredential.user;
    } catch (error) {
    return rejectWithValue(translateAuthError(error.code));
  }
});

export const logoutUser = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem(SESSION_KEY);
  await signOut(auth);
});

const initialState = {
  user: null,
  isAuth: false,
  loading: false,
  error: null,
  authReady: false,
};

const buildUser = (firebaseUser, prevUser) => {
  const sameUser = prevUser && prevUser.uid === firebaseUser.uid;
  const base = sameUser ? { ...prevUser } : {};
  // Сохраняем ВСЕ поля профиля (статус, срок статуса, имя и т.д.)
  ['displayName', 'status', 'statusUntil', 'photoURL', 'role', 'blocked', 'unreadFrom'].forEach((f) => {
    if (firebaseUser[f] !== undefined) base[f] = firebaseUser[f];
  });
  return {
    ...base,
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    photoURL: base.photoURL || null,
    role: base.role || 'user',
    blocked: !!base.blocked,
  };
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      if (action.payload) {
        state.user = buildUser(action.payload, state.user);
        state.isAuth = true;
      } else {
        state.user = null;
        state.isAuth = false;
      }
    },
    clearUser: (state) => {
      state.user = null;
      state.isAuth = false;
    },
    updateAvatar: (state, action) => {
      if (state.user) state.user.photoURL = action.payload;
    },
    updateUserFields: (state, action) => {
      if (state.user) state.user = { ...state.user, ...action.payload };
    },
    setAuthReady: (state) => {
      state.authReady = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = buildUser(action.payload, state.user);
        state.isAuth = true;
      })
      .addCase(loginUser.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(registerUser.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = buildUser(action.payload, state.user);
        state.isAuth = true;
      })
      .addCase(registerUser.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const { setUser, clearUser, updateAvatar, updateUserFields, setAuthReady } = authSlice.actions;
export default authSlice.reducer;